import React from 'react';
import { motion } from 'framer-motion';
import { Check, Star } from 'lucide-react';
import Button from '../ui/Button';

const PricingTiers: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  const freePlanFeatures = [
    'Onboarding Survey',
    'Career Compare',
    'Basic Profile'
  ];

  const premiumPlanFeatures = [
    'Skill Gap Analysis',
    'Mentorship Access',
    'Internship Opportunities data',
    'Market Trends & Salary Insights',
    'Scholarships Data',
    'Career Simulation'
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="max-w-5xl mx-auto"
        >
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">
              Choose Your Plan
            </h2>
            <p className="text-xl text-gray-600 text-center">
              Select the perfect plan for your career journey
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Free Plan */}
            <motion.div
              variants={itemVariants}
              className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Free</h3>
                  <p className="text-gray-600 mt-1">Get started with basics</p>
                </div>
                <Star className="h-10 w-10 text-gray-400" />
              </div>

              <div className="mb-8">
                <div className="text-4xl font-bold text-gray-900">₹0</div>
                <div className="text-gray-500">Forever free</div>
                <div className="text-sm text-gray-400 mt-1">$0 USD</div>
              </div>

              <ul className="space-y-4 mb-8">
                {freePlanFeatures.map((feature) => (
                  <li key={feature} className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="h-12">
                <Button
                  variant="outline"
                  fullWidth
                  className="text-lg py-4 rounded-xl border-2 border-primary-600 text-primary-600 hover:bg-primary-50 transition-all duration-300 shadow-sm hover:shadow-md"
                >
                  Get Started
                </Button>
              </div>
            </motion.div>

            {/* Premium Plan */}
            <motion.div
              variants={itemVariants}
              className="bg-white rounded-2xl shadow-lg p-8 border-2 border-primary-500 hover:shadow-xl transition-all duration-300 relative"
            >
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-primary-600 text-white px-6 py-2 rounded-full text-sm font-medium shadow-md">
                  Most Popular
                </span>
              </div>

              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Premium</h3>
                  <p className="text-gray-600 mt-1">For serious career growth</p>
                </div>
                <Star className="h-10 w-10 text-primary-500" />
              </div>

              <div className="mb-8">
                <div className="text-4xl font-bold text-gray-900">₹999</div>
                <div className="text-gray-500">per month</div>
                <div className="text-sm text-gray-400 mt-1">$12 USD</div>
              </div>

              <ul className="space-y-4 mb-8">
                {premiumPlanFeatures.map((feature) => (
                  <li key={feature} className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="h-12">
                <Button
                  fullWidth
                  className="text-lg py-4 bg-primary-600 text-white hover:bg-primary-700 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-[1.02]"
                >
                  Upgrade to Premium
                </Button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingTiers;

export { PricingTiers };